import { useEffect, useState, useSyncExternalStore } from 'react'
import { User } from '../../../types'
import { loadUser } from '../../actions/loadUser'
import { socketLookupUser } from '../../actions/socketLookupUser'
import { loadUserExtras } from '../../actions/user'
import { UserEvent } from '../../constants/enums'
import { pubSubUsers } from '../../state/pubSub'
import { getStateUser, getUser, resolveID } from '../../state/state.users'

export interface Options {
    allowNullIdentifier?: boolean
    socketLookup?: boolean
    skipLookup?: boolean
    loadExtras?: boolean
    loadNotes?: boolean
}

type userSelectorType<
    B extends boolean,
    T extends keyof User,
    J extends keyof User[T],
> = B extends true ? User[T][J] : User[T]

const getUserSelector =
    <T extends keyof User, K extends boolean, J extends keyof User[T]>(
        id: number,
        key: T,
        deepKey: K,
        deepAttribute?: J,
    ) =>
    (): userSelectorType<K, T, J> =>
        deepKey ? getUser(id)?.[key]?.[deepAttribute] : getUser(id)?.[key]

const subUserAttribute = (id: number, attribute: string) => (callback) => {
    if (id === undefined) {
        // don't actually sub if we don't have an id yet -
        // Connections, PinDragHelper, and QuickMenu (potentially others) may all trigger this during normal use
        return
    }
    return pubSubUsers.on(
        UserEvent.UPDATE + '::' + id + '::' + attribute,
        callback,
    )
}

/**
 * Retrieves user attribute from `USERSSTATE`. Does not trigger any lookups by default
 */
export const useUserSelector = <
    T extends keyof User,
    J extends keyof User[T],
    B extends boolean = false,
>(
    slugOrId: string | number | 'me',
    attribute: T,
    { socketLookup = false, lookup = false } = {},
    deepKey?: B, // adding this was the easiest way I could find to ensure types worked
    deepAttribute?: J,
): userSelectorType<B, T, J> => {
    const [foundId, setFoundId] = useState(resolveID(slugOrId))

    useEffect(() => {
        if (!foundId && USERS_LOADING[slugOrId]) {
            USERS_LOADING[slugOrId].push(setFoundId)
        }
    }, [foundId, slugOrId])

    useEffect(() => {
        if (slugOrId !== 'me') {
            setFoundId(resolveID(slugOrId))
            return
        }

        const callback = () => {
            setFoundId(resolveID(slugOrId))
        }

        return pubSubUsers.on(UserEvent.ME_ID, callback)
    }, [slugOrId])

    useEffect(() => {
        if ((socketLookup || lookup) && slugOrId) {
            // check if the model exists and then do a lookup if not
            const id = resolveID(slugOrId)
            const setFoundIdAndState = (id: number) => {
                setFoundId(id)
            }
            const exists = id ? !!getStateUser(id) : false
            if (exists) {
                setFoundId(id)
            } else if (!USERS_LOADING[slugOrId]) {
                USERS_LOADING[slugOrId] = [setFoundIdAndState]
                const lookupUser = socketLookup ? socketLookupUser : loadUser
                lookupUser(slugOrId).then(() => {
                    const id = resolveID(slugOrId)
                    USERS_LOADING[slugOrId].forEach((callback) => callback(id))
                    // eslint-disable-next-line fp/no-delete
                    delete USERS_LOADING[slugOrId]
                })
            } else if (USERS_LOADING[slugOrId]) {
                // if we're loading then we need to ensure we still get subscribed
                USERS_LOADING[slugOrId].push(setFoundIdAndState)
            }
        }
    }, [slugOrId, socketLookup, lookup])

    const getSnapshot = getUserSelector(
        foundId,
        attribute,
        deepKey,
        deepAttribute,
    )

    return useSyncExternalStore(
        subUserAttribute(foundId, attribute),
        getSnapshot,
        getSnapshot,
    )
}

const USERS_LOADING = {} // keep track of loading models so we don't accidentally load twice

export const useUserExists = (
    slugOrId: string | number,
    { socketLookup = false, loadExtras = false } = {},
): [loading: boolean, exists: boolean] => {
    const [userId, setUserId] = useState(() => getUser(slugOrId)?.id)

    const [loading, setLoading] = useState(() => !userId)
    const [extras, setExtras] = useState(false)
    const [exists, setExists] = useState(() => (!userId ? undefined : true))

    useEffect(() => {
        if (!slugOrId) return
        if (!USERS_LOADING[slugOrId]) {
            const lookupUser = socketLookup ? socketLookupUser : loadUser
            const id = resolveID(slugOrId)
            const setIdAndState = (id: number) => {
                setUserId(id)
                setLoading(false)
                setExists(true)
            }
            const existsInState = id ? !!getStateUser(id) : false
            if (!existsInState) {
                USERS_LOADING[slugOrId] = [setIdAndState]
                lookupUser(slugOrId).then((exists) => {
                    if (exists) {
                        const u = getStateUser(resolveID(slugOrId))
                        if (u) {
                            USERS_LOADING[slugOrId].forEach((callback) =>
                                callback(u.id),
                            )
                        }
                    } else {
                        setUserId(undefined)
                        setLoading(false)
                        setExists(false)
                    }
                    // eslint-disable-next-line fp/no-delete
                    delete USERS_LOADING[slugOrId]
                })
            } else {
                setExists(true)
                setLoading(false)
                setUserId(id)
            }
        } else if (USERS_LOADING[slugOrId]) {
            // if we're loading then we need to ensure we still get updated
            USERS_LOADING[slugOrId].push(setUserId)
        }
    }, [slugOrId, userId, socketLookup])

    useEffect(() => {
        /// if the id changes we need to reset the state, so old/incorrect values aren't provided
        const existingId = getUser(slugOrId)?.id
        setUserId(() => existingId)
        setExists(() => (!existingId ? undefined : true))
    }, [slugOrId])

    useEffect(() => {
        if (!userId) return
        return () => {
            // ID has changed so let's reset the loading extras state
            setExtras(false)
        }
    }, [userId])

    useEffect(() => {
        if (!userId) return
        if (!extras && !getUser(slugOrId)?.extras && loadExtras) {
            setExtras(true)
            loadUserExtras(userId)
        }
    }, [slugOrId, userId, extras, loadExtras])

    return [loading, exists]
}
